import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { L94Rfil94Service } from '@/core/service/l94_rfil_94.service';
import { L94Rfil94 } from '@/core/models/models/pages/l94rfil94';
import { DeviatoioService } from '@/core/service/deviatoio.service';

type Mode = 'new' | 'edit' | 'view';

enum TipologiaId {
    Tipo1 = 1,
    Tipo2 = 2,
    Tipo3 = 3
}

interface TabConfig {
    sRows: number[];
    conditions: string[];
    includePQ: boolean;
    includeRTVZ: boolean;
}

@Component({
    selector: 'app-scheda-l94-form',
    standalone: true,
    providers: [MessageService],
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonModule, DividerModule, TableModule, InputTextModule, SelectModule, TooltipModule, TabsModule, MessageModule, ToastModule],
    templateUrl: './scheda-l94-form.component.html',
    styleUrl: './scheda-l94-form.component.scss'
})
export class SchedaL94FormComponent implements OnInit {
    mode: Mode = 'new';
    saving = false;

    /** Identificazione condivisa (sigla/dettaglio/tipologia): una sola per tutti i tab. */
    headerForm!: FormGroup;
    /** Form per-tab: solo misure (S, condizioni, P/Q/R/T/V/Z) + id/tab. */
    forms: Record<string, FormGroup> = {};

    readonly operators = [
        { label: '<', value: '<' },
        { label: '<=', value: '<=' },
        { label: '=', value: '=' },
        { label: '>', value: '>' },
        { label: '>=', value: '>=' }
    ];

    tipologiaOptions: any[] = [];
    selectedTipologiaId: number | null = null;
    activeTab = 'a';

    /** Sezione Identificazione aperta: si comprime quando si sceglie la tipologia */
    identOpen = true;

    /** Latch: una volta sbloccate, le Misurazioni restano visibili anche se poi
     *  si modifica la Sigla (evita che spariscano durante la modifica). */
    private misureUnlocked = false;

    readonly TipologiaId = TipologiaId;

    constructor(
        private fb: FormBuilder,
        private l94Service: L94Rfil94Service,
        private deviatoioService: DeviatoioService,
        private messageService: MessageService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loadTipologiaOptions();

        const id = this.route.snapshot.paramMap.get('id');
        const qpMode = this.route.snapshot.queryParamMap.get('mode');

        if (!id) {
            this.mode = 'new';
            this.selectedTipologiaId = null;
            this.activeTab = 'a';
            this.identOpen = true;
            this.misureUnlocked = false;
            this.buildHeaderForm();
            this.forms = { a: this.createFormForTab() };
        } else {
            this.mode = qpMode === 'view' ? 'view' : 'edit';
            this.loadItem(Number(id));
        }
    }

    get isNew(): boolean {
        return this.mode === 'new';
    }
    get isReadonly(): boolean {
        return this.mode === 'view';
    }
    get title(): string {
        return this.mode === 'new' ? 'Nuova tolleranza L94' : this.mode === 'view' ? 'Dettaglio tolleranza L94' : 'Modifica tolleranza L94';
    }
    get sigla(): string {
        return (this.headerForm?.get('sigla')?.value as string) ?? '';
    }
    get tipologiaLabel(): string {
        const id = this.headerForm?.get('tipologia')?.value ?? this.selectedTipologiaId;
        return this.tipologiaOptions.find((o) => o.id === id)?.labelFe ?? '';
    }
    /** Le Misurazioni si aprono quando Sigla + Tipologia sono valorizzate, poi
     *  restano sbloccate (latch) per non sparire durante la modifica della Sigla. */
    get canMisure(): boolean {
        if (!this.misureUnlocked && !!this.selectedTipologiaId && !!this.sigla?.trim()) {
            this.misureUnlocked = true;
        }
        return this.misureUnlocked;
    }
    /** Salva abilitato solo se identificazione + tutti i tab misure sono validi. */
    get formsValid(): boolean {
        const forms = Object.values(this.forms);
        return !!this.headerForm?.valid && forms.length > 0 && forms.every((f) => f.valid);
    }

    // ==================== Data ====================

    private loadTipologiaOptions(): void {
        this.deviatoioService.getTipologia().subscribe({
            next: (options) => (this.tipologiaOptions = options),
            error: (err) => console.error('Errore caricamento tipologie', err)
        });
    }

    private loadItem(id: number): void {
        this.l94Service.getL94Rfil94Byid(id).subscribe({
            next: (items) => {
                if (!items || items.length === 0) {
                    this.showError('Nessun dato trovato per la sigla selezionata');
                    return;
                }
                if (items.length === 1) this.selectedTipologiaId = 1;
                if (items.length === 3) this.selectedTipologiaId = 2;
                if (items.length === 4) this.selectedTipologiaId = 3;

                this.buildHeaderForm(items[0]);

                this.forms = {};
                items.forEach((relatedItem) => {
                    const tabKey = this.getTabKeyFromNumber(Number(relatedItem.tab));
                    this.forms[tabKey] = this.createFormForTab(relatedItem, tabKey);
                });

                // tipologia già nota → identificazione compressa, misure già sbloccate
                this.identOpen = false;
                this.misureUnlocked = true;

                if (this.isReadonly) {
                    setTimeout(() => {
                        this.headerForm.disable();
                        Object.values(this.forms).forEach((f) => f.disable());
                    }, 50);
                }
            },
            error: (err) => {
                console.error('Errore caricamento dettagli L94:', err);
                this.showError('Errore nel caricamento dei dati');
            }
        });
    }

    // ==================== Form ====================

    /** Identificazione condivisa: una sola sigla/dettaglio/tipologia per tutti i tab. */
    private buildHeaderForm(item?: L94Rfil94): void {
        this.headerForm = this.fb.group({
            sigla: [item?.sigla ?? '', Validators.required],
            dettaglio: [item?.dettaglio ?? ''],
            tipologia: [
                {
                    value: item?.tipologia ?? this.selectedTipologiaId,
                    disabled: this.mode === 'edit' || this.mode === 'view'
                },
                Validators.required
            ]
        });
    }

    private createFormForTab(item?: L94Rfil94, _tabKey?: string): FormGroup<any> {
        const form = this.fb.group<any>({
            id: [item?.id ?? null],
            tab: [item?.tab ?? null]
        });

        const conditionControls = this.createConditionControls(item);
        const measurementControls = this.createMeasurementControls(item);
        const sControls = this.createSValueControls(item);

        // Le misure sono facoltative ai fini del salvataggio: nessun validatore
        // required qui (gli obbligatori sono solo Sigla + Tipologia nell'headerForm).
        Object.entries({ ...conditionControls, ...measurementControls, ...sControls }).forEach(([key, control]) => {
            form.addControl(key, this.fb.control(control[0]));
        });

        form.updateValueAndValidity();
        return form;
    }

    private createConditionControls(item?: L94Rfil94): Record<string, any> {
        const conditions = ['A', 'B', 'C', 'D', 'E', 'F'];
        return conditions.reduce(
            (acc, cond) => {
                const lowerCond = cond.toLowerCase();
                acc[lowerCond] = [item?.[lowerCond as keyof L94Rfil94] ?? ''];
                acc[`op${cond}`] = [item?.[`op${cond}` as keyof L94Rfil94] ?? ''];
                return acc;
            },
            {} as Record<string, any>
        );
    }

    private createMeasurementControls(item?: L94Rfil94): Record<string, any> {
        const measurements = ['p', 'q', 'r', 't', 'v', 'z'];
        return measurements.reduce(
            (acc, measure) => {
                acc[measure] = [item?.[measure as keyof L94Rfil94] ?? null];
                return acc;
            },
            {} as Record<string, any>
        );
    }

    private createSValueControls(item?: L94Rfil94): Record<string, any> {
        const controls: Record<string, any> = {};
        for (let i = 1; i <= 12; i++) {
            controls[`s${i}`] = [item?.[`s${i}` as keyof L94Rfil94] ?? null];
            controls[`minS${i}`] = [item?.[`minS${i}` as keyof L94Rfil94] ?? null];
            controls[`plusS${i}`] = [item?.[`plusS${i}` as keyof L94Rfil94] ?? null];
        }
        return controls;
    }

    private getTabKeyFromNumber(tabNum: number): string {
        const keys = ['a', 'b', 'c', 'd'];
        return keys[tabNum - 1] || 'a';
    }

    get currentForm(): FormGroup {
        if (!this.forms[this.activeTab]) {
            this.forms[this.activeTab] = this.createFormForTab(undefined, this.activeTab);
        }
        return this.forms[this.activeTab];
    }

    onTipologiaChange(tipologiaId: number): void {
        this.selectedTipologiaId = tipologiaId;
        this.activeTab = 'a';
        // comprime l'Identificazione solo se la Sigla è già valorizzata,
        // altrimenti la tiene aperta per permettere di compilarla
        this.identOpen = !(tipologiaId && this.sigla?.trim());

        // ricostruisce solo i form-misure dei tab (l'identità sta nell'headerForm)
        this.forms = {};
        this.getTabKeysForTipologia(tipologiaId).forEach((key) => {
            this.forms[key] = this.createFormForTab(undefined, key);
        });
    }

    private getTabKeysForTipologia(tipologiaId: number): string[] {
        switch (tipologiaId) {
            case TipologiaId.Tipo1:
                return ['a'];
            case TipologiaId.Tipo2:
                return ['a', 'b', 'c'];
            case TipologiaId.Tipo3:
                return ['a', 'b', 'c', 'd'];
            default:
                return [];
        }
    }

    // ==================== Save ====================

    salva(): void {
        const allFormsValid = Object.values(this.forms).every((form) => form.valid);
        if (!allFormsValid) {
            this.showError('Compila tutti i campi obbligatori in tutti i tab');
            return;
        }

        this.saving = true;

        if (this.mode === 'edit') {
            const header = this.headerForm.getRawValue();
            const updates = Object.values(this.forms).map((form) =>
                this.l94Service.update({ ...form.value, sigla: header.sigla, dettaglio: header.dettaglio, tipologia: this.selectedTipologiaId })
            );
            forkJoin(updates).subscribe({
                next: () => this.onSaved('Elementi aggiornati con successo'),
                error: (err) => this.onSaveError(err)
            });
        } else {
            this.l94Service.create(this.buildPayloadFromForms()).subscribe({
                next: (created) => this.onSaved('Elementi creati con successo', created),
                error: (err) => this.onSaveError(err)
            });
        }
    }

    private onSaved(detail: string, created?: L94Rfil94[]): void {
        this.saving = false;
        this.showSuccess(detail);

        // Resta sulla pagina (NON torna alla tabella).
        // Se era una creazione, passa alla modifica del record appena creato
        // (così i salvataggi successivi sono update e non duplicano).
        if (this.mode === 'new') {
            const newId = created?.[0]?.id;
            if (newId != null) {
                this.router.navigate(['/scheda-l94', newId], { replaceUrl: true });
            }
        }
    }

    private onSaveError(err: any): void {
        this.saving = false;
        console.error('Errore salvataggio L94', err);
        this.showError('Errore durante il salvataggio');
    }

    annulla(): void {
        this.router.navigate(['/scheda-l94']);
    }

    private buildPayloadFromForms(): L94Rfil94[] {
        const result: L94Rfil94[] = [];
        const tabMapping: Record<string, number> = { a: 1, b: 2, c: 3, d: 4 };
        const header = this.headerForm.getRawValue();

        Object.entries(this.forms).forEach(([tabKey, form]) => {
            const formValue = form.value as L94Rfil94;
            const config = this.getTabConfig(tabKey);

            const item: L94Rfil94 = {
                sigla: header.sigla!,
                dettaglio: header.dettaglio!,
                tab: String(tabMapping[tabKey]),
                tipologia: this.selectedTipologiaId!
            };

            for (const cond of config.conditions) {
                const lower = cond.toLowerCase() as keyof L94Rfil94;
                const op = `op${cond}` as keyof L94Rfil94;
                item[lower] = formValue[lower] ?? '';
                item[op] = formValue[op] ?? '';
            }
            for (const i of config.sRows) {
                item[`s${i}` as keyof L94Rfil94] = formValue[`s${i}` as keyof L94Rfil94] ?? 0;
                item[`minS${i}` as keyof L94Rfil94] = formValue[`minS${i}` as keyof L94Rfil94] ?? 0;
                item[`plusS${i}` as keyof L94Rfil94] = formValue[`plusS${i}` as keyof L94Rfil94] ?? 0;
            }
            if (config.includePQ) {
                item.p = formValue.p ?? 0;
                item.q = formValue.q ?? 0;
            }
            if (config.includeRTVZ) {
                item.r = formValue.r ?? 0;
                item.t = formValue.t ?? 0;
                item.v = formValue.v ?? 0;
                item.z = formValue.z ?? 0;
            }

            result.push(this.cleanObject(item));
        });

        return result;
    }

    private cleanObject<T extends Record<string, any>>(obj: T): T {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                (acc as any)[key] = value;
            }
            return acc;
        }, {} as T);
    }

    // ==================== Helpers ====================

    private showSuccess(detail: string): void {
        this.messageService.add({ severity: 'success', summary: 'Operazione completata', detail });
    }
    private showError(detail: string): void {
        this.messageService.add({ severity: 'error', summary: 'Errore', detail });
    }

    getTabConfig(tab: string): TabConfig {
        if (this.selectedTipologiaId === TipologiaId.Tipo1) {
            return { sRows: [1, 2, 3, 4, 5, 6, 7, 8], conditions: ['A', 'B', 'C', 'D'], includePQ: true, includeRTVZ: false };
        }
        if (this.selectedTipologiaId === TipologiaId.Tipo2) {
            switch (tab) {
                case 'a':
                case 'b':
                    return { sRows: [1, 2, 3, 4, 5, 6, 7, 8], conditions: ['A', 'B', 'C', 'D'], includePQ: true, includeRTVZ: false };
                case 'c':
                    return { sRows: [7, 8], conditions: [], includePQ: true, includeRTVZ: false };
            }
        }
        if (this.selectedTipologiaId === TipologiaId.Tipo3) {
            switch (tab) {
                case 'a':
                    return { sRows: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], conditions: ['A', 'B', 'C', 'D', 'E', 'F'], includePQ: true, includeRTVZ: true };
                case 'b':
                case 'd':
                    return { sRows: [1, 2, 3, 4, 5, 6], conditions: ['A', 'B', 'C', 'D'], includePQ: false, includeRTVZ: false };
                case 'c':
                    return { sRows: [1, 2, 3, 4, 5, 6, 7, 8], conditions: ['A', 'B', 'C', 'D'], includePQ: true, includeRTVZ: false };
            }
        }
        return { sRows: [], conditions: [], includePQ: false, includeRTVZ: false };
    }
}
